import { generate } from 'randomstring'
import { FC, useCallback, useMemo, useState } from 'react'
import { Point, useChart } from 'components/atoms/chart/useChart'
import { ResizeHandler } from 'handlers/ResizeHandler/ResizeHandler'
import { ChartLine } from 'components/atoms/chart/ChartLine/ChartLine'
import styles from 'components/features/creditCard/creditCardComparisonChart/Chart/Chart.styl'
import { ChartLineArea } from 'components/atoms/chart/ChartLineArea/ChartLineArea'
import last from 'lodash/last'
import { cssVar } from 'helpers/styleHelpers'
import { ChartBottomAxis } from 'components/features/creditCard/creditCardComparisonChart/ChartBottomAxis/ChartBottomAxis'

const X_AXIS_HEIGHT = 30

type ChartProps = {
  statusData: number[],
  comparisonData: number[],
}
export const Chart: FC<ChartProps> = (props) => {
  const { statusData, comparisonData } = props

  const [size, setSize] = useState({ width: 0, height: 0 })
  const handleSize = useCallback((nextSize: ClientRect) => {
    const { width, height } = nextSize

    setSize({
      width,
      height: height - X_AXIS_HEIGHT,
    })
  }, [])
  const statusPoints = useMemo(() => {
    return statusData.map((value, index) => ([index, value])) as Point[]
  }, [statusData])

  const comparisonPoints = useMemo(() => {
    return comparisonData.map((value, index) => ([index, value])) as Point[]
  }, [comparisonData])

  const chartPoints = useMemo(() => {
    return [
      ...statusPoints,
      ...comparisonPoints,
    ].sort((a, b) => {
      return a[0] - b[0]
    })
  }, [comparisonPoints, statusPoints])


  const context = useChart(chartPoints, size.width, size.height)

  const axisPoints = useMemo(() => {
    return new Array(6).fill(0).map((_, index) => ([index, 0])) as Point[]
  }, [])
  const axisContext = useChart(axisPoints, size.width, size.height)

  const { statusContext, comparisonContext } = useMemo(() => ({
    statusContext: {
      ...context,
      points: statusPoints,
    },
    comparisonContext: {
      ...context,
      points: comparisonPoints,
    },
  }), [comparisonPoints, context, statusPoints])

  const cashGradId = useMemo(generate, [])
  const gradChild = useMemo(() => (
    <linearGradient
      id={ cashGradId }
      x1='0%'
      x2='0%'
      y1='100%'
      y2='0%'
    >
      <stop offset='0%' stopColor='#66c8ec' stopOpacity='0' />
      <stop offset='100%' stopColor='#66c8ec' stopOpacity='0.85' />
    </linearGradient>
  ), [cashGradId])

  return (
    <ResizeHandler
      className={ styles.chart }
      onChange={ handleSize }
      style={ cssVar({
        '--x-axis-height': `${ X_AXIS_HEIGHT }px`,
      }) }
    >
      <svg className={ styles.svg }>
        { gradChild }
        <ChartLineArea
          context={ statusContext }
          className={ styles.area1 }
          style={{
            fill: `url(#${ cashGradId })`,
          }}
        />
        <ChartBottomAxis context={ axisContext } className={ styles.xAxis } key={ `line${ size.width }-${ size.height }` } />
        <ChartLine
          context={ statusContext }
          className={ styles.line1 }
        />
        <ChartLine
          context={ comparisonContext }
          className={ styles.line2 }
        />
        <circle
          className={ styles.statusPoint }
          cx={ context.scaleX(last(statusPoints)[0]) }
          cy={ context.scaleY(last(statusPoints)[1]) }
          r={ 6 }
        />
        <circle
          className={ styles.comparisonPoint }
          cx={ context.scaleX(last(comparisonPoints)[0]) }
          cy={ context.scaleY(last(comparisonPoints)[1]) }
          r={ 6 }
        />
      </svg>
    </ResizeHandler>
  )
}
